import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

/**
 * Hook to get user's VIP, Admin, and Internal Tester status.
 * 
 * Account Types:
 * - Admin (is_admin=true): Backend dashboard access, auto-VIP
 * - Internal Tester (is_internal_tester=true): Normal customer flow, auto-VIP (no ads, no prompts)
 * - Regular User: Normal customer flow, respects subscription status
 * 
 * Usage:
 * const { isVip, isAdmin, isInternalTester, loading, refresh } = useUserStatus();
 */
export const useUserStatus = () => {
  const [isVip, setIsVip] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInternalTester, setIsInternalTester] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionExpiry, setSubscriptionExpiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/user/profile');
      setIsVip(response.data.is_vip);
      setIsAdmin(response.data.is_admin || false);
      setIsInternalTester(response.data.is_internal_tester || false);
      setSubscriptionPlan(response.data.subscription_plan);
      setSubscriptionExpiry(response.data.subscription_expiry);
      setError(null);
    } catch (err) {
      console.log('User status check failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Computed properties
  // shouldShowAds: false if user is VIP, Admin, or Internal Tester
  const shouldShowAds = !isVip && !isAdmin && !isInternalTester;
  
  // shouldShowUpgradePrompts: false if user is VIP, Admin, or Internal Tester
  const shouldShowUpgradePrompts = !isVip && !isAdmin && !isInternalTester;
  
  // hasFullAccess: true if user is VIP, Admin, or Internal Tester
  const hasFullAccess = isVip || isAdmin || isInternalTester;

  return {
    isVip,
    isAdmin,
    isInternalTester,
    subscriptionPlan,
    subscriptionExpiry,
    loading,
    error,
    refresh: fetchStatus,
    // Convenience flags for UI logic
    shouldShowAds,
    shouldShowUpgradePrompts,
    hasFullAccess,
  };
};

export default useUserStatus;
